import { useEffect, useRef, useState } from "react";
import {
  IconBriefcase,
  IconMoonStars,
  IconCoins,
  IconCompass,
  IconHeart,
  IconUsersGroup,
} from "@tabler/icons-react";
import loveImg from "../../assets/global/uc_love.webp";
import familyImg from "../../assets/global/uc_marriage.webp";
import careerImg from "../../assets/global/uc_career.webp";
import moneyImg from "../../assets/global/uc_money.webp";
import lifeImg from "../../assets/global/uc_life.webp";
import birthImg from "../../assets/global/uc_birthchart.webp";
import styles from "./GlobalUseCases.module.css";

const CASES = [
  {
    Icon: IconHeart,
    title: "Love & Relationship",
    body: "Understand relationship patterns, compatibility, emotional confusion, and future direction.",
    image: loveImg,
  },
  {
    Icon: IconUsersGroup,
    title: "Marriage & Family",
    body: "Guidance on marriage timing, family pressure, partner concerns, and compatibility.",
    image: familyImg,
  },
  {
    Icon: IconBriefcase,
    title: "Career & Job",
    body: "Ask about career growth, job change, business decisions, and professional challenges.",
    image: careerImg,
  },
  {
    Icon: IconCoins,
    title: "Money & Finance",
    body: "Understand financial phases, opportunities, delays, and better timing for decisions.",
    image: moneyImg,
  },
  {
    Icon: IconCompass,
    title: "Life Direction",
    body: "Get clarity when you feel stuck, confused, or unsure about your next step.",
    image: lifeImg,
  },
  {
    Icon: IconMoonStars,
    title: "Your Birth Chart",
    body: "Your exact natal chart read and explained, planets, houses, dashas, and what they mean for you.",
    image: birthImg,
  },
];

const N = CASES.length;

export default function GlobalUseCases() {
  const [active, setActive] = useState(0);
  const paused = useRef(false);
  const isDesktop = useRef(
    typeof window === "undefined"
      ? true
      : !window.matchMedia("(max-width: 860px)").matches
  );

  // Desktop only: gentle auto-advance + hover-to-open, paused while hovering.
  // Mobile: tap to open only, no auto-advance, first card stays open by default.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 860px)");
    const sync = () => (isDesktop.current = !mq.matches);
    sync();
    mq.addEventListener?.("change", sync);

    const id = window.setInterval(() => {
      if (isDesktop.current && !paused.current) setActive((a) => (a + 1) % N);
    }, 4200);

    return () => {
      window.clearInterval(id);
      mq.removeEventListener?.("change", sync);
    };
  }, []);

  return (
    <section className={styles.section} aria-labelledby="usecases-heading">
      <h2 id="usecases-heading" className={styles.heading}>
        Ask Aura AI About Anything That Matters to You
      </h2>

      <div
        className={styles.accordion}
        onMouseEnter={() => (paused.current = true)}
        onMouseLeave={() => (paused.current = false)}
      >
        {CASES.map((c, i) => {
          const Icon = c.Icon;
          const on = i === active;
          return (
            <button
              key={c.title}
              type="button"
              className={styles.panel}
              data-active={on}
              aria-label={c.title}
              aria-pressed={on}
              onMouseEnter={() => isDesktop.current && setActive(i)}
              onFocus={() => isDesktop.current && setActive(i)}
              onClick={() => setActive(i)}
            >
              <img
                className={styles.panelImg}
                src={c.image}
                alt=""
                loading="lazy"
                decoding="async"
              />
              <span className={styles.panelShade} />
              <span className={styles.panelFoot}>
                <span className={styles.panelBadge}>
                  <Icon size={24} />
                </span>
                <span className={styles.panelLabel}>
                  <span className={styles.panelKicker}>Ask Aura AI about</span>
                  <span className={styles.panelTitle}>{c.title}</span>
                  <span className={styles.panelBody}>{c.body}</span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
